import { SETTING_PANEL_TABS } from '@/constants/settingPanels';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import eventBus from '@/utils/eventBus';
import { computed, onMounted, onUnmounted, ref } from 'vue';

/**
 * Composable for converting between units based on project settings
 */
export function useUnitConversion() {
  const cruxModuleStore = useCruxModuleStore();

  // Get the current length unit from project settings
  const lengthUnit = computed(() => {
    const settings = cruxModuleStore.getModuleByType(SETTING_PANEL_TABS.PROJECT_SETTINGS);
    return settings?.data?.['units.length'] || 'meters';
  });

  // Get the current angle unit from project settings
  const angleUnit = computed(() => {
    const settings = cruxModuleStore.getModuleByType(SETTING_PANEL_TABS.PROJECT_SETTINGS);
    return settings?.data?.['units.angle'] || 'degrees';
  });

  // Get the current photometric unit from project settings
  const photometricUnit = computed(() => {
    const settings = cruxModuleStore.getModuleByType(SETTING_PANEL_TABS.PROJECT_SETTINGS);
    return settings?.data?.['units.photometric'] || 'metric';
  });

  // Force re-computation trigger for reactive updates
  const updateTrigger = ref(0);

  // Listen for unit changes
  const handleUnitChange = (value) => {
    updateTrigger.value++;
  };

  onMounted(() => {
    eventBus.on('units:length:changed', handleUnitChange);
    eventBus.on('units:angle:changed', handleUnitChange);
    eventBus.on('units:photometric:changed', handleUnitChange);
  });

  onUnmounted(() => {
    eventBus.off('units:length:changed', handleUnitChange);
    eventBus.off('units:angle:changed', handleUnitChange);
    eventBus.off('units:photometric:changed', handleUnitChange);
  });

  /**
   * Convert meters to the project's length unit
   * @param {number} meters - Value in meters
   * @param {number} decimals - Number of decimal places (default: 0)
   * @returns {string} Formatted value with unit label
   */
  const formatLength = (meters, decimals = 0) => {
    const value = convertLength(meters);
    return `${value.toFixed(decimals)} ${getLengthUnitLabel()}`;
  };

  /**
   * Convert meters to the project's length unit (value only)
   * @param {number} meters - Value in meters
   * @returns {number} Converted value
   */
  const convertLength = (meters) => {
    // Access updateTrigger to make this reactive
    updateTrigger.value; // eslint-disable-line no-unused-expressions

    const conversions = {
      millimeters: 1000,
      centimeters: 100,
      meters: 1,
      inches: 39.3701,
      feet: 3.28084,
    };

    return meters * (conversions[lengthUnit.value] || conversions.millimeters);
  };

  /**
   * Get the label for the current length unit
   * @returns {string} Unit label (e.g., 'mm', 'cm', 'm')
   */
  const getLengthUnitLabel = () => {
    // Access updateTrigger to make this reactive
    updateTrigger.value; // eslint-disable-line no-unused-expressions

    const labels = {
      millimeters: 'mm',
      centimeters: 'cm',
      meters: 'm',
      inches: 'in',
      feet: 'ft',
    };

    return labels[lengthUnit.value] || 'm';
  };

  /**
   * Convert the project's length unit back to meters
   * @param {number} value - Value in project's length unit
   * @returns {number} Value in meters
   */
  const toMeters = (value) => {
    const conversions = {
      millimeters: 0.001,
      centimeters: 0.01,
      meters: 1,
      inches: 0.0254,
      feet: 0.3048,
    };

    return value * (conversions[lengthUnit.value] || conversions.millimeters);
  };

  /**
   * Convert radians to the project's angle unit
   * @param {number} radians - Value in radians
   * @returns {number} Converted value
   */
  const convertAngle = (radians) => {
    // Access updateTrigger to make this reactive
    updateTrigger.value; // eslint-disable-line no-unused-expressions

    const conversions = {
      degrees: 180 / Math.PI,
      radians: 1,
    };

    return radians * (conversions[angleUnit.value] || conversions.degrees);
  };

  /**
   * Convert degrees (storage format) to the project's angle unit
   * @param {number} degrees - Value in degrees (as stored in data)
   * @returns {number} Converted value
   */
  const convertAngleFromDegrees = (degrees) => {
    // Access updateTrigger to make this reactive
    updateTrigger.value; // eslint-disable-line no-unused-expressions

    if (angleUnit.value === 'radians') {
      // Convert degrees to radians for display
      return degrees * (Math.PI / 180);
    }
    // Already in degrees, return as is
    return degrees;
  };

  /**
   * Get the label for the current angle unit
   * @returns {string} Unit label (e.g., '°', 'rad')
   */
  const getAngleUnitLabel = () => {
    // Access updateTrigger to make this reactive
    updateTrigger.value; // eslint-disable-line no-unused-expressions

    const labels = {
      degrees: '°',
      radians: 'rad',
    };

    return labels[angleUnit.value] || '°';
  };

  /**
   * Convert the project's angle unit back to radians
   * @param {number} value - Value in project's angle unit
   * @returns {number} Value in radians
   */
  const toRadians = (value) => {
    const conversions = {
      degrees: Math.PI / 180,
      radians: 1,
    };

    return value * (conversions[angleUnit.value] || conversions.degrees);
  };

  /**
   * Convert the project's angle unit back to degrees (storage format)
   * @param {number} value - Value in project's angle unit
   * @returns {number} Value in degrees
   */
  const toDegrees = (value) => {
    // Access updateTrigger to make this reactive
    updateTrigger.value; // eslint-disable-line no-unused-expressions

    if (angleUnit.value === 'radians') {
      // Convert radians to degrees for storage
      return value * (180 / Math.PI);
    }
    // Already in degrees, return as is
    return value;
  };

  /**
   * Convert Three.js intensity value to display photometric unit
   * Three.js stores intensity where 1.0 = 100,000 lux
   * @param {number} intensity - Three.js intensity value (1.0 = 100,000 lux)
   * @returns {number} Converted value in display units (lux or foot-candles)
   */
  const convertPhotometricFromMetric = (intensity) => {
    // Access updateTrigger to make this reactive
    updateTrigger.value; // eslint-disable-line no-unused-expressions

    // Convert Three.js intensity to lux
    const lux = intensity * 100000;

    if (photometricUnit.value === 'imperial') {
      // Convert lux to foot-candles (1 foot-candle = 10.764 lux)
      return lux / 10.764;
    }
    // Return lux for metric
    return lux;
  };

  /**
   * Get the label for the current photometric unit
   * @param {string} type - 'illuminance' or 'luminance' (default: 'illuminance')
   * @returns {string} Unit label (e.g., 'lx', 'fc', 'cd/m²', 'fL')
   */
  const getPhotometricUnitLabel = (type = 'illuminance') => {
    // Access updateTrigger to make this reactive
    updateTrigger.value; // eslint-disable-line no-unused-expressions

    if (type === 'luminance') {
      const labels = {
        metric: 'cd/m²',
        imperial: 'fL',
      };
      return labels[photometricUnit.value] || 'cd/m²';
    }

    // Default to illuminance
    const labels = {
      metric: 'lx',
      imperial: 'fc',
    };

    return labels[photometricUnit.value] || 'lx';
  };

  /**
   * Convert display photometric unit back to Three.js intensity value
   * Three.js stores intensity where 1.0 = 100,000 lux
   * @param {number} value - Value in display units (lux or foot-candles)
   * @returns {number} Three.js intensity value
   */
  const toMetricPhotometric = (value) => {
    // Access updateTrigger to make this reactive
    updateTrigger.value; // eslint-disable-line no-unused-expressions

    let lux = value;

    if (photometricUnit.value === 'imperial') {
      // Convert foot-candles to lux (1 foot-candle = 10.764 lux)
      lux = value * 10.764;
    }

    // Convert lux to Three.js intensity (1.0 = 100,000 lux)
    return lux / 100000;
  };

  return {
    lengthUnit,
    angleUnit,
    photometricUnit,
    formatLength,
    convertLength,
    getLengthUnitLabel,
    toMeters,
    convertAngle,
    convertAngleFromDegrees,
    getAngleUnitLabel,
    toRadians,
    toDegrees,
    convertPhotometricFromMetric,
    getPhotometricUnitLabel,
    toMetricPhotometric,
  };
}
